// Výpis rubriky blogu (issue #34, screens/20-blog.md, mockupy
// design/„Blog výpis.dc.html“ + „Blog výpis mobil.dc.html“).
//
// `/rubriky/<slug>/`, `body.type-posts-listing`. Nativní strom popisuje
// blogModel.js — restylujeme ho a dostavujeme jen to, co v něm chybí:
// lištu rubrik (z nativních `ul.subcategories`), meta a CTA karet, číselnou
// paginaci a boční sloupec. Vše visí na `html.bs-blog-ready`, takže bez JS
// zůstane funkční nativní výpis.
//
// ROZHODNUTÍ (data dev instance, 2026-07-29):
//   • Lišta rubrik NENÍ statický blok ze specky — Shoptet sourozenecké rubriky
//     renderuje sám (`ul.subcategories`), aktuální doplníme z H1.
//   • Perex rubriky se bere z nativního `.sectionDescription` (admin ho může
//     vyplnit); prázdný = žádný perex, vlastní text bychom psali za redakci.
//   • „Nejčtenější“ z designu nemá zdroj (Shoptet návštěvnost článků nevystavuje)
//     → box je „Nejnovější v rubrice“ a staví se jen na 1. straně, kde jsou
//     nejnovější články opravdu v DOM.
//   • Newsletter v boxu neduplikuje nativní form (`#formNewsletterWidget` je
//     v patičce a druhý by rozbil ID i souhlasy) — vlastní pole svou hodnotu
//     přelije do nativního pole a odešle NATIVNÍ form, takže souhlasy, honeypot
//     i `formId` zůstávají jediné. Bez nativního formu se box vrátí k odkazu.
//
// AKTUALIZACE (paritní pass U6, #47/#48/#51, 2026-08-14): datum, doba čtení
// a počty článků cizích rubrik se už nevypouštějí — dotahují se ze stránek,
// kde v DOM reálně jsou (detail článku, výpis cizí rubriky). Fetche jedou až
// v nečinnosti, po dvou, a odpovědi se ukládají (blogModel `hydrate`/cache),
// takže druhá návštěva blogu je bez sítě. Když fetch selže, karta prostě
// zůstane bez meta řádku — layout na něm nestojí.

import { icon, iconHtml } from './icon.js';
import {
  articlesWord,
  fetchArticleMeta,
  fetchRubricTotal,
  hydrate,
  pageInfo,
  pageUrlFactory,
  parseArticles,
  readRubrics,
  rubricIcon,
  textOf,
  tidyPerex,
  totalArticles,
  whenIdle,
} from './blogModel.js';
import L from '../locale/cs/articlesPage.js';

const READY = 'bs-blog-ready';

// Texty → locale vrstva (S3 #399); dluh per soubor měří tools/lib/locale-debt.mjs
const LABEL_LEAD = L.lead;
const LABEL_LEAD_CTA = L.leadCta;
const LABEL_MORE = L.more;
const LABEL_RUBRICS = L.rubrics;
const LABEL_LATEST = L.latest;
const LABEL_PREV = L.prev;
const LABEL_NEXT = L.next;
const NEWSLETTER = {
  eyebrow: L.newsletterEyebrow,
  title: L.newsletterTitle,
  text: L.newsletterText,
  cta: L.newsletterCta,
  submit: L.newsletterSubmit,
  placeholder: L.newsletterPlaceholder,
  label: L.newsletterLabel,
};

const LATEST_LIMIT = 4;

function el(tag, cls, txt) {
  const node = document.createElement(tag);
  if (cls) node.className = cls;
  if (txt != null) node.textContent = txt;
  return node;
}

function link(href, cls, txt) {
  const node = el('a', cls, txt);
  node.href = href;
  return node;
}

function isListing() {
  return document.body.classList.contains('type-posts-listing');
}

// ---------------------------------------------------------------------------
// Hlavička: H1 + reálný počet článků + (volitelný) perex z administrace
// ---------------------------------------------------------------------------
function buildHead(inner) {
  const desc = inner.querySelector('.sectionDescription');
  if (!desc || desc.classList.contains('bs-blog__head')) return;
  desc.classList.add('bs-blog__head');

  const h1 = desc.querySelector('h1');
  if (h1) h1.classList.add('bs-blog__title');

  const total = totalArticles();
  if (h1 && total != null) {
    h1.insertAdjacentElement('afterend', el('span', 'bs-blog__count', total + ' ' + articlesWord(total)));
  }

  // perex = zbytek nativního popisu rubriky (admin), ne náš vymyšlený text
  const blocks = desc.querySelectorAll(':scope > p, :scope > div');
  for (let i = 0; i < blocks.length; i += 1) {
    if (textOf(blocks[i])) blocks[i].classList.add('bs-blog__perex');
  }
}

// ---------------------------------------------------------------------------
// Lišta rubrik — restyl nativního `ul.subcategories` + doplněná aktivní pilulka
// ---------------------------------------------------------------------------
// Počet článků rubriky — u aktivní ho zná stránka (`.itemsTotal`), u ostatních
// dorazí z jejich výpisu. Číslo se přidává do KAŽDÉHO místa rubriky (pilulka
// v liště i řádek v boxu „Rubriky“), proto sbíráme cíle podle href.
function setCount(host, cls, total) {
  if (total == null || host.querySelector('.' + cls)) return;
  host.appendChild(el('span', cls, String(total)));
}

function buildRubricBar(inner, rubrics) {
  const list = inner.querySelector('ul.subcategories');
  if (!list || list.classList.contains('bs-blog__rubrics')) return;
  list.classList.add('bs-blog__rubrics');

  for (let i = 0; i < rubrics.length; i += 1) {
    const r = rubrics[i];
    if (!r.li) continue;
    r.li.className = 'bs-blog__rub';
    r.link.classList.add('bs-blog__rub-link');
    r.link.setAttribute('data-bs-rub', r.href);
    r.link.insertAdjacentElement('afterbegin', icon(rubricIcon(r.href), 'bs-blog__rub-icon'));
  }

  const active = rubrics.length ? rubrics[0] : null;
  if (active && active.active) {
    const li = el('li', 'bs-blog__rub is-active');
    const a = link(active.href, 'bs-blog__rub-link');
    a.setAttribute('aria-current', 'page');
    a.appendChild(icon(rubricIcon(active.href), 'bs-blog__rub-icon'));
    a.appendChild(el('span', 'text', active.name));
    setCount(a, 'bs-blog__rub-count', totalArticles());
    li.appendChild(a);
    list.insertAdjacentElement('afterbegin', li);
    // mobilní lišta je vodorovný scroller — aktivní pilulka musí být celá
    // vidět i tam, kde by ji zarovnání nechalo pod levou hranou (#52)
    if (li.scrollIntoView) li.scrollIntoView({ block: 'nearest', inline: 'start' });
  }
}

// Úkoly pro dohydratování počtů — jeden fetch na rubriku, výsledek se rozdá
// do všech jejích uzlů (pilulka + řádek v boxu).
function countTasks(rubrics) {
  const tasks = [];
  for (let i = 0; i < rubrics.length; i += 1) {
    const r = rubrics[i];
    if (r.active || !r.href) continue;
    tasks.push({
      href: r.href,
      load: fetchRubricTotal,
      apply: function (total) {
        const nodes = document.querySelectorAll('[data-bs-rub]');
        for (let j = 0; j < nodes.length; j += 1) {
          if (nodes[j].getAttribute('data-bs-rub') !== r.href) continue;
          const cls = nodes[j].classList.contains('bs-blog__rrow')
            ? 'bs-blog__rrow-count'
            : 'bs-blog__rub-count';
          setCount(nodes[j], cls, total);
        }
      },
    });
  }
  return tasks;
}

// ---------------------------------------------------------------------------
// Karty článků — nativní `article.news-item` jen doplníme o chip, meta a CTA
// ---------------------------------------------------------------------------
function enhanceCard(item, rubricName, lead) {
  // guard je NAŠE třída, ne `data-bs-*` — offline fixtures se z atributů čistí
  if (item.querySelector('.bs-acard__body')) return;
  item.dataset.bsCard = '1';
  item.classList.add('bs-acard');
  if (lead) item.classList.add('bs-acard--lead');

  const media = item.querySelector('a.news-item-link-img');
  const title = item.querySelector('h2');
  const desc = item.querySelector('.description');
  const titleLink = title ? title.querySelector('a') : null;
  const href = titleLink ? titleLink.getAttribute('href') : null;

  if (title) title.classList.add('bs-acard__title');
  if (desc) {
    desc.classList.add('bs-acard__perex');
    // nativní perex je useknutý na znaky — doříznout na větu/slovo (#53)
    const p = desc.querySelector('p');
    if (p) p.textContent = tidyPerex(textOf(p));
  }
  if (media) {
    media.classList.add('bs-acard__media');
    if (lead) media.appendChild(el('span', 'bs-acard__badge', LABEL_LEAD));
    else if (rubricName) media.appendChild(el('span', 'bs-acard__chip', rubricName));
  }

  // tělo karty (meta + titulek + perex + CTA) — nativní uzly se STĚHUJÍ, ne kopírují
  // Meta je na kartě vždy; desktop ji u mřížkových karet schová (rubrika sedí
  // jako chip na fotce), mobilní řádková karta naopak schová chip a nechá meta.
  const body = el('div', 'bs-acard__body');
  const meta = el('span', 'bs-acard__meta');
  if (rubricName) meta.appendChild(el('span', 'bs-acard__rub', rubricName));
  body.appendChild(meta);
  if (title) body.appendChild(title);
  if (desc) body.appendChild(desc);
  item.appendChild(body);

  if (href) {
    const cta = link(href, 'bs-acard__cta', lead ? LABEL_LEAD_CTA : LABEL_MORE);
    cta.appendChild(icon('arrow-right'));
    cta.setAttribute('tabindex', '-1'); // na článek už vede fotka i titulek
    cta.setAttribute('aria-hidden', 'true');
    body.appendChild(cta);
  }
}

// Datum a doba čtení dorazí z detailu článku (ve výpisu v DOM nejsou).
// Meta řádek se zpřístupní až s daty (`is-meta`), aby na kartách nezůstala
// prázdná mezera, kdyby fetch selhal.
function applyCardMeta(item, meta) {
  const row = item.querySelector('.bs-acard__meta');
  if (!row || item.classList.contains('is-meta')) return;
  const lead = item.classList.contains('bs-acard--lead');
  const parts = [];
  if (meta.date) parts.push(el('span', 'bs-acard__date', meta.date));
  const read = lead ? meta.read : meta.readShort;
  if (read) parts.push(el('span', 'bs-acard__read', read));
  if (!parts.length) return;
  for (let i = 0; i < parts.length; i += 1) {
    if (row.childNodes.length) row.appendChild(el('span', 'bs-acard__sep', '·'));
    row.appendChild(parts[i]);
  }
  item.classList.add('is-meta');
}

function buildCards(wrap, rubricName, withLead) {
  wrap.classList.add('bs-blog__cards');
  if (withLead) wrap.classList.add('bs-blog__cards--lead');
  const items = wrap.querySelectorAll('.news-item');
  const tasks = [];
  for (let i = 0; i < items.length; i += 1) {
    const item = items[i];
    enhanceCard(item, rubricName, withLead && i === 0);
    const link2 = item.querySelector('h2 a');
    const href = link2 ? link2.getAttribute('href') : null;
    if (href) {
      tasks.push({
        href: href,
        load: fetchArticleMeta,
        apply: function (meta) {
          applyCardMeta(item, meta);
        },
      });
    }
  }
  return tasks;
}

// ---------------------------------------------------------------------------
// Paginace — Shoptet renderuje jen aktuální/další/poslední, čísla dopočítáme
// z rozsahu (`p#paginationStatus`) a šablony odkazu (`…/strana-6/`).
// ---------------------------------------------------------------------------
function pagerArrow(dir, href) {
  const node = href ? link(href, 'bs-blog__parrow') : el('span', 'bs-blog__parrow is-disabled');
  node.classList.add('bs-blog__parrow--' + dir);
  if (href) node.setAttribute('aria-label', dir === 'prev' ? LABEL_PREV : LABEL_NEXT);
  else node.setAttribute('aria-hidden', 'true');
  node.innerHTML = iconHtml(dir === 'prev' ? 'arrow-left' : 'arrow-right');
  return node;
}

// 1 … n−1 n n+1 … poslední (bez duplicit, aktuální vždy uprostřed)
function pageWindow(page, pages) {
  const set = {};
  const add = function (n) {
    if (n >= 1 && n <= pages) set[n] = true;
  };
  add(1);
  add(pages);
  add(page - 1);
  add(page);
  add(page + 1);
  // na krajích dorovnat okno na tři čísla (design má „1 2 3 … 6")
  if (page === 1) add(3);
  if (page === pages) add(pages - 2);
  return Object.keys(set)
    .map(Number)
    .sort(function (a, b) {
      return a - b;
    });
}

function buildPager(controls) {
  const nav = controls.querySelector('nav.pagination');
  if (!nav || controls.querySelector('.bs-blog__pager')) return;
  const info = pageInfo();
  if (!info.pages || info.pages < 2) return;

  const url = pageUrlFactory();
  nav.classList.add('bs-blog__pag-native'); // nativní seznam schová CSS

  const pager = el('nav', 'bs-blog__pager');
  pager.setAttribute('aria-label', L.pagerLabel);
  pager.appendChild(pagerArrow('prev', info.page > 1 ? url(info.page - 1) : null));

  const list = el('div', 'bs-blog__pages');
  const nums = pageWindow(info.page, info.pages);
  let last = 0;
  for (let i = 0; i < nums.length; i += 1) {
    const n = nums[i];
    if (n - last > 1) list.appendChild(el('span', 'bs-blog__pgap', '…'));
    if (n === info.page) {
      const cur = el('span', 'bs-blog__pnum is-current', String(n));
      cur.setAttribute('aria-current', 'page');
      list.appendChild(cur);
    } else {
      list.appendChild(link(url(n), 'bs-blog__pnum', String(n)));
    }
    last = n;
  }
  pager.appendChild(list);
  pager.appendChild(el('span', 'bs-blog__plabel', L.pageOf(info.page, info.pages)));
  pager.appendChild(pagerArrow('next', info.page < info.pages ? url(info.page + 1) : null));

  nav.insertAdjacentElement('afterend', pager);
}

// ---------------------------------------------------------------------------
// Boční sloupec — na mobilu ho Shoptet neservíruje vůbec, proto ho stavíme
// uvnitř `#content` a nativní `aside.sidebar` schová CSS.
// ---------------------------------------------------------------------------
function sideCard(title) {
  const card = el('div', 'bs-blog__card');
  card.appendChild(el('div', 'bs-blog__card-title', title));
  return card;
}

function buildRubricList(side, rubrics) {
  if (rubrics.length < 2) return;
  const card = sideCard(LABEL_RUBRICS);
  const list = el('div', 'bs-blog__rlist');
  for (let i = 0; i < rubrics.length; i += 1) {
    const r = rubrics[i];
    const row = link(r.href, 'bs-blog__rrow' + (r.active ? ' is-active' : ''));
    row.setAttribute('data-bs-rub', r.href);
    row.appendChild(icon(rubricIcon(r.href), 'bs-blog__rrow-icon'));
    row.appendChild(el('span', 'bs-blog__rrow-name', r.name));
    if (r.active) setCount(row, 'bs-blog__rrow-count', totalArticles());
    list.appendChild(row);
  }
  card.appendChild(list);
  side.appendChild(card);
}

// Pořadí v rubrice = pořadí ve výpisu; jinde než na 1. straně by titulek
// „nejnovější“ lhal, tak se box vůbec nestaví.
//
// Titulek zůstává „Nejnovější v rubrice“, i když mockup psal „Nejčtenější“ —
// čtenost článků Shoptet nikde nenese a název by lhal (rozhodnutí Tomáše
// 2026-08-30, triage #115). Meta řádek pod titulkem mockup má a doplňujeme ho:
// veze RUBRIKU (u výpisu je to ta aktivní), dobu čtení ne — z výpisu je
// k dispozici jen zkrácený perex a odhad by lhal. Stejné pravidlo jako u meta
// řádku karty článku na homepage.
function buildLatest(side, wrap, page, rubrics) {
  if (page !== 1) return;
  const items = parseArticles(wrap).slice(1, 1 + LATEST_LIMIT);
  if (items.length < 2) return;

  const active = (rubrics || []).find(function (r) {
    return r.active;
  });
  const meta = active ? active.name : null;

  const card = sideCard(LABEL_LATEST);
  const list = el('div', 'bs-blog__latest');
  for (let i = 0; i < items.length; i += 1) {
    const row = link(items[i].href, 'bs-blog__lrow');
    row.appendChild(el('span', 'bs-blog__lnum', String(i + 1)));
    const text = el('span', 'bs-blog__ltext');
    text.appendChild(el('span', 'bs-blog__lname', items[i].title));
    if (meta) text.appendChild(el('span', 'bs-blog__lmeta', meta));
    row.appendChild(text);
    list.appendChild(row);
  }
  card.appendChild(list);
  side.appendChild(card);
}

function nativeNewsletter() {
  return document.getElementById('formNewsletterWidget');
}

function focusNewsletter(e) {
  const form = nativeNewsletter();
  if (!form) return;
  e.preventDefault();
  form.scrollIntoView({ block: 'center', behavior: 'smooth' });
  const input = form.querySelector('input[type="email"]');
  if (input) {
    window.setTimeout(function () {
      input.focus();
    }, 420);
  }
}

// Odeslání jede PŘES nativní form v patičce: naše pole jen přelije hodnotu
// do `#newsletterWidget` a spustí nativní submit, takže souhlasy (`consents[]`),
// honeypot i `formId` zůstávají jediné a validované Shoptetem.
function submitNewsletter(e, input) {
  const form = nativeNewsletter();
  const target = form ? form.querySelector('input[type="email"]') : null;
  e.preventDefault();
  if (!form || !target) return;
  const value = (input.value || '').trim();
  if (!value) {
    input.focus();
    return;
  }
  target.value = value;
  if (form.requestSubmit) form.requestSubmit();
  else form.submit();
}

function buildNewsletter(side) {
  const card = el('div', 'bs-blog__nl');
  card.appendChild(el('span', 'bs-blog__nl-eyebrow', NEWSLETTER.eyebrow));
  card.appendChild(el('span', 'bs-blog__nl-title', NEWSLETTER.title));
  card.appendChild(el('span', 'bs-blog__nl-text', NEWSLETTER.text));

  const native = nativeNewsletter();
  if (!native) {
    // bez nativního formu (jiná patička/jazyk) zůstává původní odkaz na kotvu
    const cta = link('#formNewsletterWidget', 'bs-blog__nl-cta', NEWSLETTER.cta);
    cta.appendChild(icon('arrow-right'));
    cta.addEventListener('click', focusNewsletter);
    card.appendChild(cta);
    side.appendChild(card);
    return;
  }

  const form = el('form', 'bs-blog__nl-form');
  form.setAttribute('novalidate', 'novalidate');
  const input = document.createElement('input');
  input.type = 'email';
  input.className = 'bs-blog__nl-input';
  input.placeholder = NEWSLETTER.placeholder;
  input.setAttribute('aria-label', NEWSLETTER.label);
  input.autocomplete = 'email';
  input.required = true;
  // `bs-btn` = opt-out z globálního pravidla pro `button[type=submit]`
  // v `_buttons.scss` (jinak by tlačítko dostalo výšku 44 místo 46/52)
  const btn = el('button', 'bs-btn bs-blog__nl-cta', NEWSLETTER.submit);
  btn.type = 'submit';
  btn.appendChild(icon('arrow-right'));
  form.appendChild(input);
  form.appendChild(btn);
  form.addEventListener('submit', function (e) {
    submitNewsletter(e, input);
  });
  card.appendChild(form);

  // právní věta patří k poli, ne k patičce — bereme ji z nativního formu
  const consent = native.querySelector('.consents .label, .consents p');
  if (consent) {
    const note = consent.cloneNode(true);
    note.removeAttribute('id');
    note.className = 'bs-blog__nl-note';
    card.appendChild(note);
  }
  side.appendChild(card);
}

// ---------------------------------------------------------------------------

export function initArticlesPage() {
  if (!isListing()) return;
  const inner = document.querySelector('#content .content-inner');
  const wrap = document.getElementById('newsWrapper');
  if (!inner || !wrap || inner.querySelector('.bs-blog__layout')) return;

  inner.classList.add('bs-blog');

  const rubrics = readRubrics();
  const rubricName = rubrics.length && rubrics[0].active ? rubrics[0].name : '';
  const info = pageInfo();

  buildHead(inner);
  buildRubricBar(inner, rubrics);
  const cardTasks = buildCards(wrap, rubricName, info.page === 1);

  const controls = inner.querySelector('.listingControls');
  if (controls) {
    controls.classList.add('bs-blog__controls');
    buildPager(controls);
  }

  // dvousloupcové rozvržení: nativní uzly výpisu se stěhují do našeho gridu
  const layout = el('div', 'bs-blog__layout');
  const main = el('div', 'bs-blog__main');
  const side = el('aside', 'bs-blog__side');
  wrap.insertAdjacentElement('beforebegin', layout);
  main.appendChild(wrap);
  if (controls) main.appendChild(controls);
  layout.appendChild(main);
  layout.appendChild(side);

  buildRubricList(side, rubrics);
  buildLatest(side, wrap, info.page, rubrics);
  buildNewsletter(side);

  document.documentElement.classList.add(READY);

  // datum + doba čtení karet a počty cizích rubrik až po vykreslení
  whenIdle(function () {
    hydrate(cardTasks.concat(countTasks(rubrics)));
  });
}
